import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography, Table, Space, Modal, Form, Select, Row, Col } from 'antd'
import { DeleteOutlined, PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { getAllPrices, createPrice, deletePrice } from '../../actions/priceAction'
import { CustomButton } from '../../components/ui/CustomButton'
import { CustomInput } from '../../components/ui/CustomInput'
import { showToast } from '../../utils/toast'
import Colors from '../../utils/colors'
import Constants from '../../utils/constants'
import { removeUnderScoreAndCapitalize } from '../../helpers/strings'

const { Title } = Typography

const AddPrice = () => {
  const dispatch = useDispatch()
  const { isCreated, loading: loadingCreate, error: errorCreate } = useSelector((state) => state.createPrice)
  const { loading: listLoading, prices, totalPrices } = useSelector((state) => state.allPrices)
  const { isDeleted, error: deleteError } = useSelector((state) => state.deletePrice)
  
  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(Constants.ITEMS_PER_PAGE)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [selectedPriceId, setSelectedPriceId] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    dispatch(getAllPrices({ page: currentPage, perPage: limit, mode: Constants.MODES.ALL_INDIA_TAX }))
  }, [dispatch])

  useEffect(() => {
    if (isCreated) {
      showToast('Price created successfully', 'success')
      setCreateModalVisible(false)
      form.resetFields()
      dispatch(getAllPrices({ page: 1, perPage: limit, mode: Constants.MODES.ALL_INDIA_TAX }))
    }

    if (errorCreate) {
      showToast(errorCreate, 'error')
    }
  }, [isCreated, errorCreate])

  useEffect(() => {
    if (isDeleted) {
      showToast('Price deleted successfully', 'success')
      setDeleteModalVisible(false)
      setSelectedPriceId(null)
      dispatch(getAllPrices({ page: currentPage, perPage: limit, mode: Constants.MODES.ALL_INDIA_TAX }))
    }
    if (deleteError) {
      showToast(deleteError, 'error')
    }
  }, [isDeleted, deleteError])

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (currentPage - 1) * limit + index + 1,
      width: 50
    },
    {
      title: 'Tax Mode',
      dataIndex: 'taxMode',
      key: 'taxMode',
      render: (mode) => removeUnderScoreAndCapitalize(mode),
      width: 120
    },
    {
      title: 'Seat Capacity',
      dataIndex: 'seatCapacity',
      key: 'seatCapacity',
      width: 120
    },
    {
      title: 'Price',
      dataIndex: 'price1',
      key: 'price1',
      render: (price) => `₹${price}`,
      width: 100
    },
    {
      title: 'Service Charge',
      dataIndex: 'serviceCharge',
      key: 'serviceCharge',
      render: (charge) => `₹${charge}`,
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <CustomButton 
            size="small"
            onClick={() => handleDelete(record._id)}
            className="delete-btn"
            style={{ 
              height: '28px',
              fontSize: '10px',
              width: 'auto'
            }}
          >
            <DeleteOutlined /> Delete
          </CustomButton>
        </Space>
      ),
      width: 100
    }
  ]

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current)
    dispatch(getAllPrices({ page: pagination.current, perPage: limit, mode: 'all_india_permit' }))
  }

  const handleCreateSubmit = (values) => {
    const priceData = {
      ...values,
      mode: Constants.MODES.ALL_INDIA_TAX
    }
    dispatch(createPrice(priceData))
  }

  const handleCreateCancel = () => {
    setCreateModalVisible(false)
    form.resetFields()
  }

  const handleDelete = (priceId) => {
    setSelectedPriceId(priceId)
    setDeleteModalVisible(true)
  }

  const confirmDelete = () => {
    if (selectedPriceId) {
      dispatch(deletePrice(selectedPriceId))
    }
  }

  const cancelDelete = () => {
    setDeleteModalVisible(false)
    setSelectedPriceId(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={4} style={{ color: Colors.primary, margin: 0 }}>
          All India Tax Prices
        </Title>
        <CustomButton
          type="primary"
          onClick={() => setCreateModalVisible(true)}
          className='create-btn'
          style={{ 
            height: '28px',
            fontSize: '10px',
            width: 'auto'
          }}
        >
          <PlusOutlined /> Add New Price
        </CustomButton>
      </div>
      
      <div style={{ width: '100%', overflowX: 'auto', border: `1px solid ${Colors.darkerGray}`, borderRadius: 10 }}>
        <Table
          columns={columns}
          dataSource={prices}
          loading={listLoading}
          rowKey={(record) => record._id}
          pagination={{
            current: currentPage,
            total: totalPrices,
            pageSize: limit,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} prices`,
            itemRender: (current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    backgroundColor: current === currentPage ? Colors.primary : 'transparent',
                    color: current === currentPage ? Colors.white : Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    {current}
                  </span>
                )
              }
              return originalElement
            }
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
        />
      </div>

      <Modal
        title="Add New Price"
        open={createModalVisible}
        onOk={() => form.submit()}
        onCancel={handleCreateCancel}
        okText="Create Price"
        cancelText="Cancel"
        confirmLoading={loadingCreate}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="taxMode"
                label="Tax Mode"
                rules={[{ required: true, message: 'Please select tax mode' }]}
              >
                <Select placeholder="Select tax mode">
                  {Object.values(Constants.TAX_MODES || {}).filter(mode => ['quarterly', 'yearly'].includes(mode)).map(mode => (
                    <Select.Option key={mode} value={mode}>{removeUnderScoreAndCapitalize(mode)}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="seatCapacity"
                label="Seat Capacity"
                rules={[{ required: true, message: 'Please select seat capacity' }]}
              >
                <Select placeholder="Select seat capacity">
                  {Object.values(Constants.SEAT_CAPACITY || {}).map(capacity => (
                    <Select.Option key={capacity} value={capacity}>{capacity}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="price1"
                label="Price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <CustomInput 
                  type="number"
                  placeholder="Enter price" 
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="serviceCharge"
                label="Service Charge"
                rules={[{ required: true, message: 'Please enter service charge' }]}
              >
                <CustomInput 
                  type="number"
                  placeholder="Enter service charge" 
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Delete Price"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okType="danger"
        centered
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined style={{ color: Colors.warning, fontSize: '22px', marginRight: 12 }} />
          <span>Are you sure you want to delete this price?</span>
        </div>
      </Modal>
    </div>
  )
}

export default AddPrice